import React from "react";
import { Link } from "react-router-dom";
import { Layout } from "../layouts/Layout";
import {
  INNER_ABOUT_IMG03,
  INNER_ABOUT_IMG04,
} from "../lib/assets";
import "./Industries.css";

const industryGroups = [
  {
    title: "Manufacturing & Industrial",
    description:
      "Corporate, legal and compliance support for manufacturers, industrial enterprises and growing production businesses.",
    tags: ["Manufacturing", "MSMEs", "Export Houses", "Import Companies"],
    icon: "factory",
  },
  {
    title: "Technology & Software",
    description:
      "Governance and regulatory guidance for technology-led businesses, software companies and digital service providers.",
    tags: ["Information Technology", "Software Companies", "Telecommunications"],
    icon: "technology",
  },
  {
    title: "Banking & Financial Services",
    description:
      "Specialised support for regulated and fast-evolving financial-sector organisations.",
    tags: ["FinTech", "Banking", "NBFCs", "Insurance"],
    icon: "finance",
  },
  {
    title: "Healthcare & Life Sciences",
    description:
      "Structured legal, secretarial and compliance assistance for healthcare and life-sciences organisations.",
    tags: ["Healthcare", "Hospitals", "Pharmaceuticals"],
    icon: "healthcare",
  },
  {
    title: "Education & Institutions",
    description:
      "Governance, documentation and regulatory advisory for educational bodies and institutional organisations.",
    tags: ["Education", "Universities", "Professional Firms"],
    icon: "education",
  },
  {
    title: "Infrastructure & Real Estate",
    description:
      "End-to-end corporate and legal advisory for asset-heavy businesses and project-driven organisations.",
    tags: ["Infrastructure", "Construction", "Real Estate"],
    icon: "realestate",
  },
  {
    title: "Hospitality & Tourism",
    description:
      "Practical business support for hospitality operators, travel companies and experience-led enterprises.",
    tags: ["Hospitality", "Hotels", "Travel & Tourism"],
    icon: "hospitality",
  },
  {
    title: "Media, Retail & Digital Commerce",
    description:
      "Commercial, contractual and compliance solutions for customer-facing and content-led businesses.",
    tags: ["Media & Entertainment", "E-Commerce", "Retail"],
    icon: "retail",
  },
  {
    title: "Logistics & Supply Chain",
    description:
      "Business structuring, contracting and compliance support for logistics and cross-border trade operations.",
    tags: ["Logistics", "Export Houses", "Import Companies"],
    icon: "logistics",
  },
  {
    title: "Energy & Renewables",
    description:
      "Corporate governance and regulatory support for conventional and clean-energy businesses.",
    tags: ["Energy", "Renewable Energy"],
    icon: "energy",
  },
  {
    title: "Social & Not-for-Profit Sector",
    description:
      "Formation, governance and ongoing compliance support for mission-driven organisations.",
    tags: ["NGOs", "Section 8 Companies", "Charitable Trusts", "Societies"],
    icon: "social",
  },
  {
    title: "Government & Public Sector",
    description:
      "Reliable advisory and documentation support for public institutions and government-linked organisations.",
    tags: ["Government Organizations", "Public Sector Undertakings"],
    icon: "government",
  },
  {
    title: "Promoter-Led & Growth Businesses",
    description:
      "Scalable advisory for businesses at every stage—from founder-led ventures to established corporate groups.",
    tags: ["Family Businesses", "Startups", "MSMEs"],
    icon: "growth",
  },
  {
    title: "Large & Global Corporations",
    description:
      "High-standard governance, secretarial and strategic support for complex corporate structures.",
    tags: ["Listed Companies", "Multinational Corporations"],
    icon: "global",
  },
];

const strengths = [
  {
    number: "01",
    title: "Sector-Aware Advice",
    text: "Recommendations shaped around the operating realities, risk profile and regulatory environment of your industry.",
  },
  {
    number: "02",
    title: "Integrated Expertise",
    text: "Company secretarial, legal and consultancy capabilities coordinated through one professional team.",
  },
  {
    number: "03",
    title: "Scalable Support",
    text: "Flexible solutions for startups, MSMEs, listed entities, multinational corporations and public institutions.",
  },
  {
    number: "04",
    title: "Practical Execution",
    text: "Clear documentation, disciplined follow-up and commercially workable solutions—not theoretical advice alone.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Understand Your Industry",
    text: "We study your business model, corporate structure, operating footprint and sector-specific obligations.",
  },
  {
    number: "02",
    title: "Identify Priorities",
    text: "Our team maps immediate compliance needs, legal risks, governance gaps and strategic opportunities.",
  },
  {
    number: "03",
    title: "Build the Solution",
    text: "We create a practical action plan covering documentation, filings, advisory and implementation support.",
  },
  {
    number: "04",
    title: "Execute & Coordinate",
    text: "Required actions are completed with structured communication, ownership and timeline monitoring.",
  },
  {
    number: "05",
    title: "Support Long-Term Growth",
    text: "Ongoing assistance helps your organisation stay compliant, prepared and ready for its next stage of growth.",
  },
];

const faqs = [
  {
    question: "Do you work with startups and MSMEs?",
    answer:
      "Yes. We assist startups, promoter-led businesses and MSMEs with incorporation, structuring, contracts, corporate compliance, governance and growth advisory.",
  },
  {
    question: "Can you support regulated sectors such as NBFCs and FinTech companies?",
    answer:
      "Yes. Our team provides corporate, secretarial and regulatory support based on the organisation's activities, structure and applicable requirements.",
  },
  {
    question: "Do you assist NGOs and Section 8 Companies?",
    answer:
      "Yes. We support not-for-profit organisations with formation, governance, statutory documentation, filings and continuing compliance.",
  },
  {
    question: "Can one team handle legal, secretarial and consultancy requirements?",
    answer:
      "Yes. Globiz India combines these capabilities so businesses can receive coordinated advice and implementation support through one professional relationship.",
  },
];

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M20 6 9 17l-5-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IndustryIcon = ({ type }) => {
  const paths = {
    factory: <path d="M3 21V9l6 3V8l6 4V4h6v17H3Zm4-4h2m3 0h2m3 0h2" />,
    technology: <path d="M5 4h14v11H5V4Zm-2 15h18M9 19l1-4m5 4-1-4M9 8h6m-6 3h4" />,
    finance: <path d="M3 10h18L12 4 3 10Zm2 0v8m4-8v8m6-8v8m4-8v8M3 21h18" />,
    healthcare: <path d="M12 21s-8-4.8-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 10c0 6.2-8 11-8 11Zm0-10v5m-2.5-2.5h5" />,
    education: <path d="m3 9 9-5 9 5-9 5-9-5Zm4 3v5c3 2 7 2 10 0v-5m4-3v6" />,
    realestate: <path d="M4 21V8l8-5 8 5v13M8 21v-5h8v5M8 10h2m4 0h2" />,
    hospitality: <path d="M4 20V5m0 10h16v5M7 15V9h5a4 4 0 0 1 4 4v2M4 7h4" />,
    retail: <path d="M4 9h16l-1-5H5L4 9Zm1 0v11h14V9M9 20v-6h6v6" />,
    logistics: <path d="M3 6h11v11H3V6Zm11 4h4l3 3v4h-7v-7ZM7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />,
    energy: <path d="M13 2 5 14h6l-1 8 9-13h-6V2Z" />,
    social: <path d="M12 21s-8-4.8-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 10c0 6.2-8 11-8 11Z" />,
    government: <path d="M3 10h18L12 4 3 10Zm2 0v8m4-8v8m6-8v8m4-8v8M3 21h18" />,
    growth: <path d="M4 19V9m5 10v-6m5 6V8m5 11V4M3 21h18M5 6l4-3 5 2 5-4" />,
    global: <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-18c2.5 2.4 3.8 5.4 3.8 9S14.5 18.6 12 21c-2.5-2.4-3.8-5.4-3.8-9S9.5 5.4 12 3ZM3.5 9h17m-17 6h17" />,
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {paths[type]}
      </g>
    </svg>
  );
};

const IndustriesPage = () => {
  return (
    
    <Layout header={4} footer={2} breadcrumb="Industries" title="Industries We Serve">
      <main className="industries-page">
        <section className="industries-intro industries-section-space">
          <div className="container">
            <div className="row align-items-center g-5">
              <div className="col-lg-6 col-md-10 mx-auto">
                <div className="industries-intro__visual" data-aos="fade-right">
                  <img
                    className="industries-intro__main-image"
                    src={INNER_ABOUT_IMG03}
                    alt="Globiz India professionals serving diverse industries"
                  />

                  <div className="industries-intro__experience-card">
                    <strong>35+</strong>
                    <span>Industry sectors supported with integrated professional expertise</span>
                  </div>

                  <div className="industries-intro__mini-card">
                    <img
                      src={INNER_ABOUT_IMG04}
                      alt="Corporate advisory across industries"
                    />
                  </div>
                </div>
              </div>

              <div className="col-lg-6" data-aos="fade-left">
                <div className="industries-section-heading">
                  <span className="industries-eyebrow">Industries We Serve</span>
                  <h2>Sector-Focused Advice for Businesses at Every Stage</h2>
                </div>

                <p className="industries-lead">
                  Our professional services extend across multiple industry sectors, helping organisations manage compliance, governance, legal risk and strategic growth with confidence.
                </p>

                <p>
                  From startups and family businesses to listed companies, regulated entities, government organisations and multinational corporations, Globiz India delivers practical solutions aligned with each sector's operating environment.
                </p>

                <div className="industries-highlight-list">
                  <div className="industries-highlight-item">
                    <span className="industries-highlight-item__icon"><CheckIcon /></span>
                    <div>
                      <h4>Integrated Professional Support</h4>
                      <p>Company secretarial, legal and consultancy services under one coordinated approach.</p>
                    </div>
                  </div>

                  <div className="industries-highlight-item">
                    <span className="industries-highlight-item__icon"><CheckIcon /></span>
                    <div>
                      <h4>Solutions Built Around Your Sector</h4>
                      <p>Advice tailored to your business model, regulatory exposure and growth priorities.</p>
                    </div>
                  </div>
                </div>

                <div className="industries-intro__actions">
                  <Link to="/contact" className="industries-btn industries-btn--primary">
                    Discuss Your Industry Needs <ArrowIcon />
                  </Link>

                  <div className="industries-trust-note">
                    <strong>360°</strong>
                    <span>Corporate advisory coverage</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      

        <section className="industries-grid-section industries-section-space">
          <div className="container">
            <div className="industries-section-heading industries-section-heading--center" data-aos="fade-up">
              <span className="industries-eyebrow">Sector Coverage</span>
              <h2>Professional Services Across Diverse Industries</h2>
              <p>
                Explore the sectors where our corporate, legal, secretarial and consultancy capabilities help organisations operate with stronger governance and greater confidence.
              </p>
            </div>

            <div className="row g-4">
              {industryGroups.map((industry, index) => (
                <div className="col-xl-4 col-md-6" key={industry.title} data-aos="fade-up" data-aos-delay={(index % 3) * 80}>
                  <article className="industry-card">
                    <div className="industry-card__top">
                      <span className="industry-card__icon"><IndustryIcon type={industry.icon} /></span>
                      <span className="industry-card__number">{String(index + 1).padStart(2, "0")}</span>
                    </div>
                    <h3>{industry.title}</h3>
                    <p>{industry.description}</p>
                    <div className="industry-card__tags">
                      {industry.tags.map((tag) => <span key={tag}>{tag}</span>)}
                    </div>
                    <Link to="/contact" className="industry-card__link">
                      Get Industry Support <ArrowIcon />
                    </Link>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="industries-strengths industries-section-space">
          <div className="container">
            <div className="row align-items-end g-4 mb-5">
              <div className="col-lg-7">
                <div className="industries-section-heading" data-aos="fade-right">
                  <span className="industries-eyebrow">Why Globiz India</span>
                  <h2>Expertise That Connects Regulation with Business Reality</h2>
                </div>
              </div>
              <div className="col-lg-5" data-aos="fade-left">
                <p className="industries-strengths__intro">
                  We do more than identify requirements. We translate complex obligations into practical actions your management team can understand and implement.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {strengths.map((item, index) => (
                <div className="col-lg-3 col-md-6" key={item.number} data-aos="fade-up" data-aos-delay={index * 80}>
                  <article className="industries-strength-card">
                    <span>{item.number}</span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="industries-process industries-section-space">
          <div className="container">
            <div className="industries-section-heading industries-section-heading--center" data-aos="fade-up">
              <span className="industries-eyebrow">How We Work</span>
              <h2>A Structured Approach to Industry-Specific Support</h2>
              <p>Every engagement follows a clear process designed to understand your sector, prioritise risk and move efficiently from advice to implementation.</p>
            </div>

            <div className="industries-process__grid">
              {processSteps.map((step, index) => (
                <article className="industries-process-card" key={step.number} data-aos="fade-up" data-aos-delay={index * 70}>
                  <div className="industries-process-card__number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                  {index < processSteps.length - 1 && <span className="industries-process-card__connector" aria-hidden="true"><ArrowIcon /></span>}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="industries-faq industries-section-space">
          <div className="container">
            <div className="row g-5 align-items-start">
              <div className="col-lg-5" data-aos="fade-right">
                <div className="industries-section-heading">
                  <span className="industries-eyebrow">Frequently Asked Questions</span>
                  <h2>Industry Support, Clearly Explained</h2>
                  <p>Speak with our team for advice tailored to your organisation, business model and applicable regulatory environment.</p>
                </div>
                <Link to="/contact" className="industries-btn industries-btn--dark">
                  Ask Our Experts <ArrowIcon />
                </Link>
              </div>

              <div className="col-lg-7" data-aos="fade-left">
                <div className="accordion industries-faq-accordion" id="industriesFaqAccordion">
                  {faqs.map((faq, index) => {
                    const id = `industriesFaq${index + 1}`;
                    return (
                      <div className="accordion-item" key={faq.question}>
                        <h3 className="accordion-header">
                          <button className={`accordion-button ${index === 0 ? "" : "collapsed"}`} type="button" data-bs-toggle="collapse" data-bs-target={`#${id}`} aria-expanded={index === 0} aria-controls={id}>
                            {faq.question}
                          </button>
                        </h3>
                        <div id={id} className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`} data-bs-parent="#industriesFaqAccordion">
                          <div className="accordion-body"><p>{faq.answer}</p></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="industries-cta">
          <div className="container">
            <div className="industries-cta__inner" data-aos="zoom-in">
              <div>
                <span className="industries-eyebrow industries-eyebrow--light">Let’s Work Together</span>
                <h2>Need Professional Support for Your Industry?</h2>
                <p>Tell us about your organisation and receive practical guidance from our corporate, legal and consultancy professionals.</p>
              </div>
              <Link to="/contact" className="industries-btn industries-btn--white">
                Schedule a Consultation <ArrowIcon />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default IndustriesPage;
